
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Package } from "@/types";
import { X } from "lucide-react";
import { formatDistanceToNow } from 'date-fns';

interface PackageHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageData: Package | null;
}

const PackageHistoryModal = ({
  open,
  onOpenChange,
  packageData
}: PackageHistoryModalProps) => {
  if (!packageData || !packageData.history) {
    return null;
  }
  
  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Package History</DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="py-4">
          <h3 className="font-semibold mb-2">
            {packageData.guestName} - Package #{String(packageData.id).padStart(3, '0')}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Tracking: {packageData.tracking}
          </p>
          
          <div className="relative">
            <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-200"></div>
            
            <div className="space-y-6">
              {packageData.history.map((event, index) => (
                <div key={index} className="relative pl-8">
                  <div className={`absolute left-2.5 -translate-x-1/2 w-3 h-3 rounded-full mt-1.5 ${
                    event.status === 'Pending' ? 'bg-blue-400' :
                    event.status === 'Delivered' ? 'bg-green-400' :
                    event.status === 'Discarded' ? 'bg-orange-400' : 'bg-gray-400'
                  }`}></div>
                  
                  <div>
                    <p className="font-medium">
                      {event.status === 'Pending' ? 'Package received' :
                       event.status === 'Delivered' ? 'Package delivered' :
                       event.status === 'Discarded' ? 'Package discarded' : 
                       event.status}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(event.date)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Updated by: {event.updatedBy}
                    </p>
                    
                    {event.status === 'Delivered' && (
                      <div className="mt-2">
                        {event.signature && (
                          <div className="mt-1">
                            <p className="text-xs text-muted-foreground mb-1">Signature:</p>
                            <img 
                              src={event.signature} 
                              alt="Signature" 
                              className="border border-gray-200 rounded max-h-24"
                            />
                          </div>
                        )}
                        {event.receiverName && (
                          <p className="text-sm mt-1">
                            Received by: <span className="font-medium">{event.receiverName}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackageHistoryModal;
